import type { ReactNode } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { ApiError } from "@/api/client";

/**
 * What a screen shows when it has nothing to show.
 *
 * Three cases, and they read differently on purpose: loading is quiet, empty
 * explains what would be here, and an error says what actually went wrong —
 * including the upstream's own message, because "Request failed" helps nobody.
 */

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 6, justifyContent: "center" }}>
      <CircularProgress size={22} />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

export function Empty({ title, hint, action }: { title: string; hint?: ReactNode; action?: ReactNode }) {
  return (
    <Paper elevation={2} sx={{ py: 6, px: 3, textAlign: "center" }}>
      <Typography variant="h5" component="p">{title}</Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mx: "auto", maxWidth: 480 }}>
          {hint}
        </Typography>
      ) : null}
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Paper>
  );
}

export function Failed({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : null;
  const forbidden = api?.status === 403;
  return (
    <Alert
      severity={forbidden ? "info" : "error"}
      action={onRetry && !forbidden ? <Button size="small" onClick={onRetry}>Retry</Button> : undefined}
    >
      <AlertTitle>
        {forbidden ? "You do not have access to this" : api?.status === 404 ? "Not found" : "That did not work"}
      </AlertTitle>
      {api?.message ?? (error instanceof Error ? error.message : String(error))}
    </Alert>
  );
}

/** The three cases in one call, for the common list-then-render shape. */
export function Async<T>({
  query, empty, children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined; refetch: () => void };
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <Loading />;
  if (query.error) return <Failed error={query.error} onRetry={() => query.refetch()} />;
  if (query.data === undefined) return empty ? <>{empty}</> : null;
  return <>{children(query.data)}</>;
}
